import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Receipt, 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign,
  FileText,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', class: 'bg-orange-100 text-orange-800', icon: Clock },
  approved: { label: 'Approved', class: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', class: 'bg-red-100 text-red-800', icon: XCircle }
};

const VendorInvoices = () => {
  const { getAuthHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const fetchInvoices = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/vendor-invoices?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      toast.error('Failed to load vendor invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const openReviewDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReview = async (approved) => {
    if (!selectedInvoice) return;
    
    setSubmittingReview(true);
    try {
      const params = new URLSearchParams();
      params.append('approved', approved);
      if (reviewNotes) params.append('review_notes', reviewNotes);
      
      const response = await fetch(
        `${API_URL}/api/vendor-invoices/${selectedInvoice.vendor_invoice_id}/review?${params}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        toast.success(`Invoice ${approved ? 'approved' : 'rejected'}`);
        setReviewDialogOpen(false);
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to review invoice');
      }
    } catch (error) {
      toast.error('Failed to review invoice');
    } finally {
      setSubmittingReview(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  // Calculate stats
  const pendingCount = invoices.filter(i => i.status === 'pending_review').length;
  const pendingTotal = invoices.filter(i => i.status === 'pending_review').reduce((sum, i) => sum + i.invoice_amount, 0);
  const approvedTotal = invoices.filter(i => i.status === 'approved').reduce((sum, i) => sum + i.invoice_amount, 0);

  return (
    <div className="space-y-6" data-testid="vendor-invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-orange-600" />
            Vendor Invoices
          </h1>
          <p className="text-muted-foreground">Review and approve vendor submitted invoices</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="vendor-invoice-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={pendingCount > 0 ? 'ring-2 ring-orange-400' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-orange-600">${pendingTotal.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved (Awaiting Payment)</p>
                <p className="text-2xl font-bold">${approvedTotal.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
                <p className="text-2xl font-bold">{invoices.length}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert for pending invoices */}
      {pendingCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
          <Clock className="h-5 w-5 text-orange-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-orange-800">{pendingCount} invoice(s) awaiting your review</p>
            <p className="text-sm text-orange-600">Total amount: ${pendingTotal.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No vendor invoices</h3>
              <p className="text-muted-foreground">Vendor invoices will appear here when submitted</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice ID</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Work Order</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow 
                    key={invoice.vendor_invoice_id} 
                    data-testid={`vendor-invoice-row-${invoice.vendor_invoice_id}`}
                    className={invoice.status === 'pending_review' ? 'bg-orange-50' : ''}
                  >
                    <TableCell className="font-mono text-sm">
                      <div>
                        <p>{invoice.vendor_invoice_id}</p>
                        {invoice.invoice_number && (
                          <p className="text-xs text-muted-foreground">#{invoice.invoice_number}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{invoice.vendor_details?.name || '-'}</p>
                        <p className="text-sm text-muted-foreground">{invoice.vendor_details?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Badge variant="outline">{invoice.work_order_id}</Badge>
                        <p className="text-sm text-muted-foreground mt-1">
                          {invoice.work_order_details?.client_name}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(invoice.submitted_at)}</TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell className="text-right font-semibold text-lg">
                      ${invoice.invoice_amount?.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {invoice.status === 'pending_review' ? (
                        <Button 
                          size="sm" 
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => openReviewDialog(invoice)}
                          data-testid={`review-invoice-${invoice.vendor_invoice_id}`}
                        >
                          Review
                        </Button>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {invoice.status === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Review Vendor Invoice</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice ID:</span>
                  <span className="font-mono">{selectedInvoice.vendor_invoice_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor:</span>
                  <span>{selectedInvoice.vendor_details?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Work Order:</span>
                  <span>{selectedInvoice.work_order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span>{selectedInvoice.work_order_details?.client_name}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold">
                  <span>Amount:</span>
                  <span className="text-green-600">${selectedInvoice.invoice_amount?.toLocaleString()}</span>
                </div>
                {selectedInvoice.notes && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground">Vendor Notes:</p>
                    <p className="text-sm">{selectedInvoice.notes}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="review_notes">Review Notes (Optional)</Label>
                <Textarea
                  id="review_notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setReviewDialogOpen(false)}
              disabled={submittingReview}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleReview(false)}
              disabled={submittingReview}
              data-testid="reject-invoice-btn"
            >
              {submittingReview ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReview(true)}
              disabled={submittingReview}
              data-testid="approve-invoice-btn"
            >
              {submittingReview ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorInvoices;
